import { authHeaders, fetchWithAuthRetry, fetchWithTimeout, getApiUrl, parseApiError, type PagedData } from './common';
import { type RunnerType } from './daemon';
import type {
  MergedRunnerModelsByType,
  RunnerModelRecord,
  RunnerModelsByType,
} from '@agentteams/runner-request/ui/lib/api';
export type {
  MergedRunnerModelOption,
  MergedRunnerModelsByType,
  RunnerModelOption,
  RunnerModelRecord,
  RunnerModelsByType,
} from '@agentteams/runner-request/ui/lib/api';

export type RunnerModelQueryParams = {
  runnerType?: RunnerType;
  search?: string;
  page?: number;
  pageSize?: number;
};

export type CreateRunnerModelInput = {
  runnerType: RunnerType;
  label: string;
  value: string;
  isPublic?: boolean;
  isEnabled?: boolean;
  sortOrder?: number;
  maxInputTokens?: number | null;
  fastModeSupported?: boolean;
  supportedEffortLevels?: string[];
};

export type UpdateRunnerModelInput = {
  label?: string;
  value?: string;
  isPublic?: boolean;
  isEnabled?: boolean;
  sortOrder?: number;
  maxInputTokens?: number | null;
  fastModeSupported?: boolean;
  supportedEffortLevels?: string[];
};

type RunnerModelsGroupedEnvelope = {
  data: RunnerModelsByType;
};

type MergedRunnerModelsGroupedEnvelope = {
  data: MergedRunnerModelsByType;
};

type RunnerModelEnvelope = {
  data: RunnerModelRecord;
};

export const fetchRunnerModels = async (): Promise<RunnerModelsByType> => {
  const response = await fetchWithTimeout(`${getApiUrl()}/api/runners/models`, {
    cache: 'no-store',
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch runner models: ${response.status}`);
  }

  const payload = (await response.json()) as RunnerModelsGroupedEnvelope;
  return payload.data;
};

export const fetchMergedRunnerModels = async (token: string, memberId: string): Promise<MergedRunnerModelsByType> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/runners/models?memberId=${encodeURIComponent(memberId)}`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch merged runner models: ${response.status}`);
  }

  const payload = (await response.json()) as MergedRunnerModelsGroupedEnvelope;
  return payload.data;
};

export const fetchRunnerModelsForDaemon = async (
  token: string,
  projectId: string,
  targetDaemonId: string,
): Promise<MergedRunnerModelsByType> => {
  const query = new URLSearchParams({ projectId, targetDaemonId });
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/runners/models?${query.toString()}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch runner models for daemon: ${response.status}`);
  }

  const payload = (await response.json()) as MergedRunnerModelsGroupedEnvelope;
  return payload.data;
};

export const fetchSystemRunnerModels = async (
  token: string,
  params?: RunnerModelQueryParams,
): Promise<PagedData<RunnerModelRecord>> => {
  const query = new URLSearchParams();
  if (params?.runnerType) {
    query.set('runnerType', params.runnerType);
  }
  if (params?.search?.trim()) {
    query.set('search', params.search.trim());
  }
  if (typeof params?.page === 'number') {
    query.set('page', String(params.page));
  }
  if (typeof params?.pageSize === 'number') {
    query.set('pageSize', String(params.pageSize));
  }

  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/runner-models${query.size > 0 ? `?${query.toString()}` : ''}`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch system runner models: ${response.status}`);
  }

  return (await response.json()) as PagedData<RunnerModelRecord>;
};

export const createSystemRunnerModel = async (
  token: string,
  input: CreateRunnerModelInput,
): Promise<RunnerModelRecord> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/runner-models`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create runner model: ${response.status}`);
  }

  const payload = (await response.json()) as RunnerModelEnvelope;
  return payload.data;
};

export const updateSystemRunnerModel = async (
  token: string,
  id: string,
  input: UpdateRunnerModelInput,
): Promise<RunnerModelRecord> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/runner-models/${id}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update runner model: ${response.status}`);
  }

  const payload = (await response.json()) as RunnerModelEnvelope;
  return payload.data;
};

export const deleteSystemRunnerModel = async (token: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/runner-models/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete runner model: ${response.status}`);
  }
};
